import { Alert, CircularProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { Link, useParams } from "react-router";

import { useAuth } from "@/providers";
import { OrganizationSettings } from "@/Settings/OrganizationSettings";
import {
  PageBreadcrumbs,
  PageLayout,
  ViewAsOrgButton,
} from "@/shared/components";
import { SystemUserOnly } from "@/shared/components/SystemUserOnly";
import { OrgRecord } from "@/shared/domain/orgs";
import { orgQueryKeys, useOrg } from "@/shared/domain/orgs/hooks";

import styles from "./Org.module.css";

function useSetOrg() {
  const queryClient = useQueryClient();

  return React.useCallback(
    function setOrg(org: OrgRecord) {
      // `OrganizationSettings` owns the tier-update control but isn't wired to
      // TanStack Query yet, so it can't invalidate the cache itself. We write
      // the server's updated record straight into the entry
      // `useOrg` reads so this page's snapshot stays in sync after a tier
      // change. Once `OrganizationSettings` is modernized to use the orgs
      // domain hooks, it can own this update and this stopgap can go away.
      queryClient.setQueryData(orgQueryKeys.org(org.org_id), org);
    },
    [queryClient],
  );
}

export function Org() {
  const { currentUser } = useAuth();

  const { orgId } = useParams();
  const orgQuery = useOrg(orgId);
  const setOrg = useSetOrg();

  const breadcrumbs = [
    <Link key="list" to="/admin?tab=1">
      Orgs
    </Link>,
    <span key="orgId">{orgId}</span>,
  ];

  let content: React.ReactNode;
  if (orgId === undefined) {
    content = <Alert severity="error">Missing org id in path</Alert>;
  } else if (orgQuery.isError) {
    content = (
      <Alert severity="error">
        {orgQuery.error instanceof Error
          ? orgQuery.error.message
          : "Failed to load organization."}
      </Alert>
    );
  } else if (orgQuery.isPending) {
    content = <CircularProgress />;
  } else {
    content = (
      <>
        <div className={styles.actions}>
          <ViewAsOrgButton org={orgQuery.data} variant="contained" />
        </div>
        <div className={styles.settings}>
          <OrganizationSettings
            currentOrg={orgQuery.data}
            currentUser={currentUser}
            onOrgUpdated={setOrg}
          />
        </div>
      </>
    );
  }

  return (
    <PageLayout
      title={`Roboto - Orgs - ${orgId}`}
      headerContentLeft={<PageBreadcrumbs>{breadcrumbs}</PageBreadcrumbs>}
    >
      <SystemUserOnly>{content}</SystemUserOnly>
    </PageLayout>
  );
}
